// Партии из внешних источников: типы, парсеры NDJSON/архивов,
// нормализация, определение стороны по нику. Выделено из coach-profile.
export const MAX_GAMES = 20; // решение Iefym: 10 мало для статистики

function clean(value: unknown): string {
  return String(value == null ? '' : value).trim();
}

export function normalizeNick(nick: unknown): string {
  return clean(nick).toLowerCase();
}

export function ratingOrNull(value: unknown): number | null {
  // Number(null) === 0 — null/пустота должны оставаться null, иначе
  // отброшенный рейтинг превращается в «рейтинг 0» в фактах LLM-диагноза
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface CoachOpening {
  eco?: string;
  name?: string;
}

export interface CoachGame {
  site: 'lichess' | 'chesscom' | string;
  /** Контроль: bullet | blitz | rapid | classical | … */
  speed?: string;
  id: string;
  url?: string;
  pgn: string;
  white: string;
  black: string;
  date: string;
  result: string;
  /** Причина окончания по сторонам (chess.com white/black.result; Lichess из status):
   *  win | timeout | resigned | checkmated | abandoned | … — для анализа времени (флаг). */
  whiteResult?: string;
  blackResult?: string;
  opening: CoachOpening;
  endTime?: number;
}

/** Нормализованная причина проигрыша → корзина lossTypes. */
export function lossBucket(reason?: string): string {
  const r = (reason || '').toLowerCase();
  if (r === 'timeout' || r === 'timevsinsufficient' || r === 'outoftime') return 'timeout';
  if (r === 'resigned' || r === 'resign') return 'resigned';
  if (r === 'checkmated' || r === 'mate') return 'checkmated';
  if (r === 'abandoned' || r === 'aborted' || r === 'nostart') return 'abandoned';
  return 'other';
}

export interface Nicks {
  lichess?: string;
  chesscom?: string;
  nick?: string;
  all?: string[];
}

export interface Ratings {
  blitz: number | null;
  rapid: number | null;
  classical: number | null;
}

export function parseLichessNdjson(text: string): CoachGame[] {
  return clean(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => JSON.parse(line))
    .filter((game) => clean(game.pgn))
    .map((game) =>
      normalizeGame({
        site: 'lichess',
        speed: game.speed || game.perf,
        id: game.id || game.gameId || game.site,
        url: game.url,
        // lastMoveAt/createdAt у Lichess в мс; endTime держим в секундах, как end_time у chess.com
        endTime: Math.round(Number(game.lastMoveAt || game.createdAt || 0) / 1000),
        pgn: game.pgn,
        white: game.players?.white?.user?.name || game.white || pgnTag(game.pgn, 'White'),
        black: game.players?.black?.user?.name || game.black || pgnTag(game.pgn, 'Black'),
        // причина из status (mate/resign/outoftime/…): проигравшему — status, победителю — win
        whiteResult: game.winner === 'white' ? 'win' : game.winner === 'black' ? game.status : undefined,
        blackResult: game.winner === 'black' ? 'win' : game.winner === 'white' ? game.status : undefined,
        date: pgnTag(game.pgn, 'Date') || game.createdAt,
        result: game.winner ? (game.winner === 'white' ? '1-0' : '0-1') : pgnTag(game.pgn, 'Result'),
        opening: {
          eco: game.opening?.eco || pgnTag(game.pgn, 'ECO'),
          name: game.opening?.name || pgnTag(game.pgn, 'Opening'),
        },
      }),
    );
}

export interface ChessComArchive {
  games?: {
    uuid?: string;
    url?: string;
    pgn?: string;
    white?: { username?: string; result?: string };
    black?: { username?: string; result?: string };
    end_time?: number;
    time_class?: string;
    eco?: string;
  }[];
}

export function normalizeChessComArchiveGames(archives: ChessComArchive[]): CoachGame[] {
  const games: CoachGame[] = [];
  for (const archive of archives || []) {
    for (const game of archive?.games || []) {
      if (!clean(game.pgn)) continue;
      games.push(
        normalizeGame({
          site: 'chesscom',
          speed: game.time_class,
          id: game.uuid || game.url || pgnTag(game.pgn!, 'Site'),
          url: game.url,
          pgn: game.pgn!,
          white: game.white?.username || pgnTag(game.pgn!, 'White'),
          black: game.black?.username || pgnTag(game.pgn!, 'Black'),
          whiteResult: game.white?.result,
          blackResult: game.black?.result,
          date: pgnTag(game.pgn!, 'Date') || String(game.end_time || game.time_class || ''),
          result: pgnTag(game.pgn!, 'Result'),
          opening: {
            eco: pgnTag(game.pgn!, 'ECO'),
            name: pgnTag(game.pgn!, 'Opening') || game.eco,
          },
          endTime: Number(game.end_time) || 0,
        }),
      );
    }
  }
  return games.sort((a, b) => (b.endTime || 0) - (a.endTime || 0)).slice(0, MAX_GAMES);
}

export function normalizeGame(game: Partial<CoachGame> & { pgn: string }): CoachGame {
  const pgn = clean(game.pgn);
  return {
    site: game.site || '',
    speed: game.speed,
    url: game.url,
    endTime: game.endTime,
    id: clean(game.id),
    pgn,
    white: clean(game.white) || pgnTag(pgn, 'White'),
    black: clean(game.black) || pgnTag(pgn, 'Black'),
    date: clean(game.date) || pgnTag(pgn, 'Date'),
    result: clean(game.result) || pgnTag(pgn, 'Result'),
    whiteResult: game.whiteResult,
    blackResult: game.blackResult,
    opening: {
      eco: clean(game.opening?.eco) || pgnTag(pgn, 'ECO'),
      name: clean(game.opening?.name) || pgnTag(pgn, 'Opening'),
    },
  };
}

export function pgnTag(pgn: string, tag: string): string {
  const re = new RegExp(`^\\[${tag}\\s+"([^"]*)"\\]`, 'm');
  return clean(pgn).match(re)?.[1] || '';
}

export function sideForNick(game: Pick<CoachGame, 'white' | 'black' | 'pgn'> | { white?: string; black?: string; pgn?: string }, nicks: Nicks = {}): 'w' | 'b' | null {
  const wanted = [nicks.lichess, nicks.chesscom, nicks.nick, ...(nicks.all || [])]
    .map(normalizeNick)
    .filter(Boolean);
  if (!wanted.length) return null;
  const pgn = game.pgn || '';
  if (wanted.includes(normalizeNick(game.white || pgnTag(pgn, 'White')))) return 'w';
  if (wanted.includes(normalizeNick(game.black || pgnTag(pgn, 'Black')))) return 'b';
  return null;
}

// Формат ников на обеих площадках: латиница/цифры/дефис/подчёркивание.
// Валидация до fetch — мусор и попытки инъекций не уходят в URL.
export function isValidNick(nick: unknown): boolean {
  return /^[a-z0-9_-]{2,30}$/.test(normalizeNick(nick));
}

